import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const DetailedExpenseBreakdown = ({ breakdownData }) => {
    const [expandedGroups, setExpandedGroups] = useState({});

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2
        }).format(value);
    };

    const toggleGroup = (groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    if (!breakdownData || !breakdownData.breakdown) {
        return <div className="text-center text-gray-500">No expense data available</div>;
    }

    const { breakdown, totals } = breakdownData;

    // Prepare data for chart
    const chartData = Object.entries(breakdown).map(([paidBy, categories]) => ({
        name: paidBy,
        value: totals[paidBy] || 0
    }));

    const COLORS = {
        OFFICE: '#FF5733',
        DRIVER: '#3b82f6',
        OWNER: '#10b981'
    };

    return (
        <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4">
                    <div className="text-sm font-semibold text-red-800">Office Paid</div>
                    <div className="text-2xl font-bold text-red-900 text-right mt-2">
                        {formatCurrency(totals.OFFICE || 0)}
                    </div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                    <div className="text-sm font-semibold text-blue-800">Driver Paid</div>
                    <div className="text-2xl font-bold text-blue-900 text-right mt-2">
                        {formatCurrency(totals.DRIVER || 0)}
                    </div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                    <div className="text-sm font-semibold text-green-800">Owner Paid</div>
                    <div className="text-2xl font-bold text-green-900 text-right mt-2">
                        {formatCurrency(totals.OWNER || 0)}
                    </div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                    <div className="text-sm font-semibold text-purple-800">Total Expenses</div>
                    <div className="text-2xl font-bold text-purple-900 text-right mt-2">
                        {formatCurrency(totals.TOTAL || 0)}
                    </div>
                </div>
            </div>

            {/* Distribution Chart */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-[#2a2e30] mb-4">Expense Distribution by Paid By</h3>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip
                            formatter={(value) => formatCurrency(value)}
                            contentStyle={{ backgroundColor: 'white', border: '1px solid #ccc' }}
                        />
                        <Bar dataKey="value" name="Amount">
                            {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[entry.name] || '#6b7280'} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* Detailed Breakdown by Paid By */}
            {Object.entries(breakdown).map(([paidBy, categories]) => (
                categories.length > 0 && (
                    <div key={paidBy} className="bg-white rounded-lg shadow-md overflow-hidden">
                        <div
                            className="bg-gray-100 px-6 py-4 cursor-pointer hover:bg-gray-200 transition-colors"
                            onClick={() => toggleGroup(paidBy)}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    {expandedGroups[paidBy] ? (
                                        <ChevronDownIcon className="w-5 h-5 text-gray-600" />
                                    ) : (
                                        <ChevronRightIcon className="w-5 h-5 text-gray-600" />
                                    )}
                                    <h3 className="text-lg font-bold text-[#2a2e30]">
                                        {paidBy} Expenses
                                    </h3>
                                    <span className="text-sm text-gray-600">
                                        ({categories.length} {categories.length === 1 ? 'category' : 'categories'})
                                    </span>
                                </div>
                                <div className="text-xl font-bold text-[#2a2e30]">
                                    {formatCurrency(totals[paidBy] || 0)}
                                </div>
                            </div>
                        </div>

                        {expandedGroups[paidBy] && (
                            <div className="p-6 space-y-4">
                                {categories.map((category, idx) => (
                                    <CategorySection
                                        key={idx}
                                        category={category}
                                        paidBy={paidBy}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )
            ))}
        </div>
    );
};

const CategorySection = ({ category, paidBy }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 2
        }).format(value);
    };

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div
                className="bg-gray-50 px-4 py-3 cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {isExpanded ? (
                            <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                        ) : (
                            <ChevronRightIcon className="w-4 h-4 text-gray-600" />
                        )}
                        <span className="font-semibold text-[#2a2e30]">{category.category}</span>
                        <span className="text-xs text-gray-500">
                            ({category.expenses.length} {category.expenses.length === 1 ? 'item' : 'items'})
                        </span>
                    </div>
                    <div className="text-right">
                        <div className="font-bold text-[#2a2e30]">
                            {formatCurrency(category.total)}
                        </div>
                        <div className="text-xs text-gray-600">
                            {category.percentage.toFixed(1)}% of total
                        </div>
                    </div>
                </div>
            </div>

            {isExpanded && (
                <div className="p-4">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Date</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Vehicle</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Supplier</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Invoice #</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Description</th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Amount</th>
                                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {category.expenses.map((expense, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 text-sm whitespace-nowrap">
                                            {new Date(expense.date).toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric'
                                            })}
                                        </td>
                                        <td className="px-3 py-2 text-sm font-semibold">{expense.plate_no}</td>
                                        <td className="px-3 py-2 text-sm">{expense.supplier || 'N/A'}</td>
                                        <td className="px-3 py-2 text-sm">{expense.invoice_no || 'N/A'}</td>
                                        <td className="px-3 py-2 text-sm">{expense.description}</td>
                                        <td className="px-3 py-2 text-sm text-right font-semibold">
                                            {formatCurrency(expense.amount)}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            <span className={`px-2 py-1 text-xs rounded-full ${expense.reimbursement_status === 'PENDING'
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : expense.reimbursement_status === 'COMPLETED'
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                {expense.reimbursement_status || 'N/A'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DetailedExpenseBreakdown;
